import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Alert } from '@/components/ui/Alert'
import { Badge, type BadgeTone } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { ErrorState } from '@/components/ui/ErrorState'
import { Input } from '@/components/ui/Input'
import { LoadingState } from '@/components/ui/LoadingState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { BulkApplyCourtField } from '@/components/venue/BulkApplyCourtField'
import { CourtPriceSaveConfirmModal } from '@/components/venue/CourtPriceSaveConfirmModal'
import { PriceBandEditor } from '@/components/venue/PriceBandEditor'
import { PriceDateEditor } from '@/components/venue/PriceDateEditor'
import { useCourtPriceBands } from '@/hooks/useCourtPriceBands'
import { useCourtPriceDates } from '@/hooks/useCourtPriceDates'
import { useCourtRemainingWindows } from '@/hooks/useCourtRemainingWindows'
import { useCourts } from '@/hooks/useCourts'
import { useDeleteCourtPriceDate } from '@/hooks/useDeleteCourtPriceDate'
import { useSaveCourtPriceBands } from '@/hooks/useSaveCourtPriceBands'
import { useSaveCourtPriceDate } from '@/hooks/useSaveCourtPriceDate'
import { ApiError, toApiError } from '@/lib/api/errors'
import type { VenueCourtStatus } from '@/lib/venue/courtTypes'
import { PRICE_DATE_RANGE_DAYS, type CourtPriceDateItem } from '@/lib/venue/priceTypes'
import {
  addDays,
  hasPriceBandFormErrors,
  hasRowsErrors,
  misalignedBandRanges,
  summarizeBulkApply,
  toKstDate,
  toPriceBandFormState,
  toPriceBandSaveInput,
  toPriceDateRows,
  toPriceDateSaveInput,
  validateBandRows,
  validatePriceBandForm,
  type BulkApplySummary,
  type PriceBandFormErrors,
  type PriceBandFormState,
  type PriceBandRowFormState,
  type PriceBandRowsErrors,
} from '@/lib/venue/priceViewModel'
import { useVenueContext } from '@/routes/venueContext'

/** 🚨 배지 색만 프런트가 매핑한다 — 텍스트는 서버 라벨(`statusLabel`) 그대로(`CourtCard.tsx` 규칙 승계). */
const STATUS_TONE: Record<VenueCourtStatus, BadgeTone> = {
  OPEN: 'success',
  SUSPENDED: 'neutral',
  MAINTENANCE: 'warning',
}

const EMPTY_BAND_ERRORS: PriceBandFormErrors = { baseErrors: {}, dayTypeErrors: {} }
const EMPTY_ROWS_ERRORS: PriceBandRowsErrors = { rowErrors: {} }

type PricingTab = 'DAY_TYPE' | 'DATE'

/**
 * 코트 요금 설정 — 요일유형(평일/주말) 구간 · 특정 날짜 요금 · 다른 코트 일괄 적용(#868 요구 1~3).
 *
 * 🚨 저장 가능 여부(`editable`)·상한(`maxPriceBandsPerDayType`)은 GET 응답을 그대로 바인딩한다 —
 * 클라이언트가 재계산하지 않는다(`VenueCourtOperatingHoursPage` 규칙 승계).
 * 🚨 겹침 판정의 최종 판정자는 서버다. 이 화면은 ⓐ 겹치는 시각을 선택지에서 지우고 ⓑ 저장 직전
 * 사전 검증을 돌리며 ⓒ 서버 400 문장을 그대로 보여 준다(설계 D5·D9).
 */
export function VenueCourtPricingPage() {
  const { venueId } = useVenueContext()
  const { courtId: courtIdParam } = useParams<{ courtId: string }>()
  const courtId = Number(courtIdParam)
  const navigate = useNavigate()

  const query = useCourtPriceBands(venueId, courtId)
  const courtsQuery = useCourts(venueId)

  const today = toKstDate(query.data?.baseTime)
  const datesQuery = useCourtPriceDates(
    venueId,
    courtId,
    query.data ? today : '',
    query.data ? addDays(today, PRICE_DATE_RANGE_DAYS) : ''
  )

  const saveBandsMutation = useSaveCourtPriceBands(venueId, courtId)
  const saveDateMutation = useSaveCourtPriceDate(venueId, courtId)
  const deleteDateMutation = useDeleteCourtPriceDate(venueId, courtId)

  const [tab, setTab] = useState<PricingTab>('DAY_TYPE')
  const [form, setForm] = useState<PriceBandFormState | null>(null)
  /** 폼이 어느 코트의 응답으로 초기화됐는지 — 코트 Select 로 이동해도 같은 컴포넌트가 유지되기 때문이다. */
  const [formCourtId, setFormCourtId] = useState<number | null>(null)
  const [bandErrors, setBandErrors] = useState<PriceBandFormErrors>(EMPTY_BAND_ERRORS)

  const [selectedDate, setSelectedDate] = useState('')
  const [dateLabel, setDateLabel] = useState('')
  const [dateRows, setDateRows] = useState<PriceBandRowFormState[]>([])
  const [dateErrors, setDateErrors] = useState<PriceBandRowsErrors>(EMPTY_ROWS_ERRORS)

  const [bulkSelectedIds, setBulkSelectedIds] = useState<number[]>([])
  const [confirming, setConfirming] = useState<PricingTab | null>(null)
  const [generalError, setGeneralError] = useState<ApiError | null>(null)
  const [bulkSummary, setBulkSummary] = useState<BulkApplySummary | null>(null)

  const remainingQuery = useCourtRemainingWindows(venueId, courtId, tab === 'DATE' ? selectedDate : '')

  // 조회가 도착하면 폼을 초기화한다. 편집 중 재조회로 덮어쓰지 않되, **코트가 바뀌면 다시 초기화**한다.
  useEffect(() => {
    if (query.data && formCourtId !== query.data.courtId) {
      setForm(toPriceBandFormState(query.data))
      setFormCourtId(query.data.courtId)
      setBandErrors(EMPTY_BAND_ERRORS)
      setSelectedDate('')
      setDateLabel('')
      setDateRows([])
      setDateErrors(EMPTY_ROWS_ERRORS)
      setBulkSelectedIds([])
      setBulkSummary(null)
      setGeneralError(null)
    }
  }, [query.data, formCourtId])

  if (query.isLoading) {
    return (
      <>
        <PageHeader title="요금 설정" />
        <LoadingState rows={4} label="요금 설정을 불러오는 중" />
      </>
    )
  }

  if (query.isError) {
    return (
      <>
        <PageHeader title="요금 설정" />
        <ErrorState
          error={query.error}
          onRetry={() => {
            void query.refetch()
          }}
        />
      </>
    )
  }

  if (!query.data || !form) return null

  const view = query.data
  const isPending =
    saveBandsMutation.isPending || saveDateMutation.isPending || deleteDateMutation.isPending
  const editable = view.editable
  const disabled = !editable || isPending

  const courtOptions = courtsQuery.data?.courts.map((court) => ({
    courtId: court.courtId,
    name: court.name,
  })) ?? [{ courtId, name: view.courtName }]
  const bulkTargets = courtOptions.filter((court) => court.courtId !== courtId)
  const selectedTargets = bulkTargets.filter((court) => bulkSelectedIds.includes(court.courtId))
  const confirmCourtNames = [view.courtName, ...selectedTargets.map((court) => court.name)]

  const dates: CourtPriceDateItem[] = datesQuery.data?.dates ?? []

  // 운영시간에서 예약 단위가 바뀐 뒤 저장된 구간이 새 단위와 어긋나는 경우를 감지한다(`#876` 요구 3).
  // 서버가 준 원본(view/dates)을 그대로 검사한다 — 편집 중인 폼 상태가 아니다.
  const misalignedDayTypeRanges = view.dayTypes.flatMap((group) => {
    const labels = misalignedBandRanges(group.bands, view.reservationUnitMinutes)
    return labels.length > 0 ? [`${group.dayTypeLabel}: ${labels.join(', ')}`] : []
  })
  const misalignedDateRanges = dates.flatMap((item) => {
    const labels = misalignedBandRanges(item.bands, view.reservationUnitMinutes)
    return labels.length > 0 ? [`${item.date}: ${labels.join(', ')}`] : []
  })
  const misalignedRanges = [...misalignedDayTypeRanges, ...misalignedDateRanges]

  function updateDayType(dayType: string, patch: Partial<PriceBandFormState['dayTypes'][number]>) {
    setForm((current) =>
      current
        ? {
            ...current,
            dayTypes: current.dayTypes.map((group) =>
              group.dayType === dayType ? { ...group, ...patch } : group
            ),
          }
        : current
    )
  }

  function handleSelectDate(date: string) {
    setSelectedDate(date)
    const item = dates.find((candidate) => candidate.date === date)
    setDateRows(toPriceDateRows(item))
    setDateLabel(item?.label ?? '')
    setDateErrors(EMPTY_ROWS_ERRORS)
  }

  function handleSaveBandsClick() {
    if (!form) return
    const errors = validatePriceBandForm(form, view.maxPriceBandsPerDayType, view.reservationUnitMinutes)
    if (hasPriceBandFormErrors(errors)) {
      setBandErrors(errors)
      return
    }
    setBandErrors(EMPTY_BAND_ERRORS)
    setGeneralError(null)
    setBulkSummary(null)
    setConfirming('DAY_TYPE')
  }

  function handleSaveDateClick() {
    if (!selectedDate) {
      setDateErrors({ rowErrors: {}, general: '날짜를 선택해 주세요.' })
      return
    }
    if (selectedDate < today) {
      setDateErrors({ rowErrors: {}, general: '오늘 이후 날짜만 설정할 수 있습니다.' })
      return
    }
    const errors = validateBandRows(
      dateRows,
      selectedDate,
      view.maxPriceBandsPerDayType,
      view.reservationUnitMinutes
    )
    if (hasRowsErrors(errors)) {
      setDateErrors(errors)
      return
    }
    setDateErrors(EMPTY_ROWS_ERRORS)
    setGeneralError(null)
    setBulkSummary(null)
    setConfirming('DATE')
  }

  async function handleConfirm() {
    if (!form) return
    try {
      if (confirming === 'DAY_TYPE') {
        const outcome = await saveBandsMutation.mutateAsync({
          input: toPriceBandSaveInput(form),
          targets: selectedTargets,
        })
        // 서버가 정규화한 값(라벨·정렬)으로 폼을 다시 맞춘다 — 저장 응답이 곧 최신 조회다.
        setForm(toPriceBandFormState(outcome.view))
        setBulkSummary(summarizeBulkApply(outcome.results))
      } else {
        const outcome = await saveDateMutation.mutateAsync({
          date: selectedDate,
          input: toPriceDateSaveInput(dateRows, dateLabel),
          targets: selectedTargets,
          courtName: view.courtName,
        })
        setBulkSummary(summarizeBulkApply(outcome.results))
      }
      setConfirming(null)
    } catch (error) {
      setConfirming(null)
      setGeneralError(toApiError(error))
    }
  }

  async function handleDeleteDate(date: string) {
    setGeneralError(null)
    setBulkSummary(null)
    try {
      await deleteDateMutation.mutateAsync(date)
      if (selectedDate === date) {
        setSelectedDate('')
        setDateRows([])
        setDateLabel('')
      }
    } catch (error) {
      setGeneralError(toApiError(error))
    }
  }

  return (
    <>
      <PageHeader
        title="요금 설정"
        description="평일·주말 기본 요금 위에 시간대·날짜별 요금을 덮어 설정할 수 있어요."
      />

      <div className="mb-5 space-y-3">
        {!editable ? (
          <Alert tone="warning">
            <p>{view.editBlockNotice}</p>
          </Alert>
        ) : null}
        {misalignedRanges.length > 0 ? (
          <Alert tone="warning">
            <p>
              {`예약 단위가 ${view.reservationUnitLabel}로 바뀌어 이미 저장된 시간대 가격 중 일부가 새 단위와 맞지 않아요. 아래 구간을 확인하고 다시 저장해 주세요.`}
            </p>
            <ul className="mt-1 space-y-0.5">
              {misalignedRanges.map((range) => (
                <li key={range} className="text-xs">
                  {range}
                </li>
              ))}
            </ul>
          </Alert>
        ) : null}
        {generalError ? (
          <Alert tone="danger">
            <p>{generalError.message}</p>
          </Alert>
        ) : null}
        {bulkSummary ? (
          <Alert
            tone={bulkSummary.failed.length > 0 ? 'danger' : 'info'}
            action={
              bulkSummary.failed.length > 0 ? (
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={isPending}
                  onClick={() => {
                    // 실패분만 다시 대상으로 잡는다. 저장 EP 가 멱등이라 현재 코트가 다시 포함돼도 안전하다.
                    setBulkSelectedIds(bulkSummary.failed.map((result) => result.courtId))
                    setBulkSummary(null)
                  }}
                >
                  실패한 코트만 다시 선택
                </Button>
              ) : undefined
            }
          >
            <p>{bulkSummary.message}</p>
            {bulkSummary.failed.map((result) => (
              <p key={result.courtId} className="mt-1">
                {result.name}: {result.message}
              </p>
            ))}
          </Alert>
        ) : null}
      </div>

      <Card className="mb-5">
        <CardBody className="flex flex-wrap items-center gap-4">
          <div>
            <label className="mb-1.5 block text-xs font-medium text-slateish-500" htmlFor="pricing-court-select">
              코트 선택
            </label>
            <Select
              id="pricing-court-select"
              value={courtId}
              disabled={isPending}
              onChange={(event) => {
                navigate(`/venues/${venueId}/courts/${event.target.value}/pricing`)
              }}
            >
              {courtOptions.map((court) => (
                <option key={court.courtId} value={court.courtId}>
                  {court.name}
                </option>
              ))}
            </Select>
          </div>
          <Badge tone={STATUS_TONE[view.status]}>{view.statusLabel}</Badge>
          <p className="text-xs text-slateish-500">
            예약 단위 {view.reservationUnitLabel} · 요금 변경은 확정된 예약에 소급되지 않아요.
          </p>
        </CardBody>
      </Card>

      <div role="tablist" aria-label="요금 설정 축" className="mb-5 flex gap-2">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'DAY_TYPE'}
          disabled={isPending}
          className={
            tab === 'DAY_TYPE'
              ? 'rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white'
              : 'rounded-lg px-4 py-2 text-sm font-medium text-slateish-600 hover:bg-slateish-200'
          }
          onClick={() => {
            setTab('DAY_TYPE')
          }}
        >
          평일·주말
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'DATE'}
          disabled={isPending}
          className={
            tab === 'DATE'
              ? 'rounded-lg bg-brand-500 px-4 py-2 text-sm font-medium text-white'
              : 'rounded-lg px-4 py-2 text-sm font-medium text-slateish-600 hover:bg-slateish-200'
          }
          onClick={() => {
            setTab('DATE')
          }}
        >
          특정 날짜
        </button>
      </div>

      <div className="space-y-5">
        {tab === 'DAY_TYPE'
          ? form.dayTypes.map((group) => {
              const serverGroup = view.dayTypes.find((item) => item.dayType === group.dayType)
              return (
                <Card key={group.dayType}>
                  <CardHeader title={`${group.dayTypeLabel} 요금`} />
                  <CardBody className="space-y-4">
                    <div className="sm:w-1/2">
                      <label
                        className="mb-1.5 block text-sm font-medium text-ink-700"
                        htmlFor={`price-base-${group.dayType}`}
                      >
                        {group.dayTypeLabel} 기본 요금 (시간당)
                      </label>
                      <Input
                        id={`price-base-${group.dayType}`}
                        inputMode="numeric"
                        disabled={disabled}
                        value={group.baseHourlyFee}
                        aria-invalid={Boolean(bandErrors.baseErrors[group.dayType])}
                        onChange={(event) => {
                          updateDayType(group.dayType, { baseHourlyFee: event.target.value })
                        }}
                      />
                      {bandErrors.baseErrors[group.dayType] ? (
                        <p className="mt-1 text-xs text-danger-500">
                          {bandErrors.baseErrors[group.dayType]}
                        </p>
                      ) : null}
                      <p className="mt-1 text-xs text-slateish-500">
                        아래 시간대 구간이 덮지 않는 시간에 적용돼요.
                      </p>
                    </div>

                    <PriceBandEditor
                      axisLabel={group.dayTypeLabel}
                      rows={group.rows}
                      onChange={(rows) => {
                        updateDayType(group.dayType, { rows })
                      }}
                      disabled={disabled}
                      maxRows={view.maxPriceBandsPerDayType}
                      unitMinutes={view.reservationUnitMinutes}
                      errors={bandErrors.dayTypeErrors[group.dayType]}
                      notice={serverGroup?.notice ?? null}
                    />
                  </CardBody>
                </Card>
              )
            })
          : (
            <PriceDateEditor
              dates={dates}
              isLoading={datesQuery.isLoading}
              selectedDate={selectedDate}
              onSelectDate={handleSelectDate}
              minDate={today}
              label={dateLabel}
              onLabelChange={setDateLabel}
              rows={dateRows}
              onRowsChange={setDateRows}
              errors={dateErrors}
              disabled={disabled}
              maxRows={view.maxPriceBandsPerDayType}
              unitMinutes={view.reservationUnitMinutes}
              remaining={remainingQuery.data}
              onDelete={(date) => {
                void handleDeleteDate(date)
              }}
            />
          )}

        <BulkApplyCourtField
          courts={bulkTargets}
          selectedIds={bulkSelectedIds}
          onChange={setBulkSelectedIds}
          disabled={disabled}
          axisNotice={
            tab === 'DAY_TYPE'
              ? '선택한 코트의 평일·주말 요금과 시간대 구간이 지금 입력한 값으로 덮어써져요.'
              : '선택한 코트에 이 날짜의 요금만 동일하게 저장돼요.'
          }
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={isPending}
            onClick={() => {
              navigate(`/venues/${venueId}/courts`)
            }}
          >
            취소
          </Button>
          <Button
            type="button"
            disabled={disabled}
            onClick={tab === 'DAY_TYPE' ? handleSaveBandsClick : handleSaveDateClick}
          >
            변경사항 저장
          </Button>
        </div>
      </div>

      {confirming ? (
        <CourtPriceSaveConfirmModal
          axisLabel={confirming === 'DAY_TYPE' ? '평일·주말 요금' : `${selectedDate} 요금`}
          courtNames={confirmCourtNames}
          isPending={isPending}
          onCancel={() => {
            setConfirming(null)
          }}
          onConfirm={() => {
            void handleConfirm()
          }}
        />
      ) : null}
    </>
  )
}
